import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Card, 
  CardContent, 
  Typography, 
  Stack, 
  Chip,
  CircularProgress,
  LinearProgress,
  Alert,
  Divider
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import TrendingFlatIcon from '@mui/icons-material/TrendingFlat';
import PsychologyIcon from '@mui/icons-material/Psychology';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import ShowChartIcon from '@mui/icons-material/ShowChart';
import api from '../api';

/**
 * StressPrediction Component
 * Displays AI-powered predictions for stress patterns based on historical data
 * Shows peak stress hours, trends, and forecasts
 */
function StressPrediction({ deviceId }) {
  const [prediction, setPrediction] = useState(null);
  const [loading, setLoading] = useState(true);
  const theme = useTheme();
  const { t } = useTranslation();

  const fetchPrediction = React.useCallback(async () => {
    if (!deviceId) return;

    try {
      const response = await api.get(`/api/device/${deviceId}/prediction/`);
      setPrediction(response.data.prediction || null);
    } catch (error) {
      console.error('Error fetching prediction data:', error);
    } finally {
      setLoading(false);
    }
  }, [deviceId]);

  useEffect(() => {
    fetchPrediction();
  }, [fetchPrediction]);

  const getTrendIcon = (trend) => {
    switch (trend) {
      case 'increasing':
        return <TrendingUpIcon />;
      case 'decreasing':
        return <TrendingDownIcon />;
      default:
        return <TrendingFlatIcon />;
    }
  };

  const getTrendColor = (trend) => {
    switch (trend) {
      case 'increasing':
        return theme.palette.error.main;
      case 'decreasing':
        return theme.palette.success.main;
      default:
        return theme.palette.info.main;
    }
  };

  const getTrendLabel = (trend) => {
    switch (trend) {
      case 'increasing':
        return t('prediction.trend_increasing', 'Increasing');
      case 'decreasing':
        return t('prediction.trend_decreasing', 'Decreasing');
      default:
        return t('prediction.trend_stable', 'Stable');
    }
  };

  const getConfidenceColor = (confidence) => {
    switch (confidence) {
      case 'high':
        return theme.palette.success.main;
      case 'medium':
        return theme.palette.warning.main;
      default:
        return theme.palette.grey[500];
    }
  };

  const getConfidenceValue = (confidence) => {
    switch (confidence) {
      case 'high':
        return 100;
      case 'medium':
        return 60;
      default:
        return 30;
    }
  };

  const getStressLevelColor = (level) => {
    if (level < 33) return theme.palette.success.main;
    if (level < 67) return theme.palette.warning.main;
    return theme.palette.error.main;
  };

  const getStressLevelLabel = (level) => {
    if (level < 33) return t('prediction.stress_low', 'Low');
    if (level < 67) return t('prediction.stress_moderate', 'Moderate');
    return t('prediction.stress_high', 'High');
  };

  if (loading) {
    return (
      <Card variant="outlined">
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
            <CircularProgress />
          </Box>
        </CardContent>
      </Card>
    );
  }

  if (!prediction) {
    return (
      <Card variant="outlined">
        <CardContent>
          <Alert severity="info">
            {t('prediction.no_data', 'Insufficient data for predictions. More usage data needed.')}
          </Alert>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card variant="outlined">
      <CardContent>
        <Stack direction="row" alignItems="center" spacing={1} mb={2}>
          <PsychologyIcon color="primary" />
          <Typography variant="h6">
            {t('prediction.title', 'Stress Pattern Prediction')}
          </Typography>
        </Stack>

        {/* Prediction Message */}
        <Alert 
          severity={prediction.trend === 'increasing' ? 'warning' : 'info'}
          icon={getTrendIcon(prediction.trend)}
          sx={{ mb: 2 }}
        >
          <Typography variant="body2">
            {prediction.message}
          </Typography>
        </Alert>

        <Stack spacing={2}>
          {/* Average Stress Level */}
          <Box>
            <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1}>
              <Typography variant="body2" color="text.secondary">
                {t('prediction.avg_stress', 'Average Stress Level')}
              </Typography>
              <Chip
                label={getStressLevelLabel(prediction.average_stress_level)}
                size="small"
                sx={{
                  bgcolor: `${getStressLevelColor(prediction.average_stress_level)}20`,
                  color: getStressLevelColor(prediction.average_stress_level),
                  fontWeight: 600,
                }}
              />
            </Stack>
            <Box sx={{ position: 'relative', mb: 2 }}>
              <LinearProgress
                variant="determinate"
                value={prediction.average_stress_level}
                sx={{
                  height: 8,
                  borderRadius: 1,
                  bgcolor: theme.palette.grey[200],
                  '& .MuiLinearProgress-bar': {
                    bgcolor: getStressLevelColor(prediction.average_stress_level),
                    borderRadius: 1,
                  }
                }}
              />
              <Typography
                variant="caption"
                sx={{
                  position: 'absolute',
                  right: 0,
                  bottom: -25,
                  fontWeight: 600,
                  color: getStressLevelColor(prediction.average_stress_level),
                }}
              >
                {prediction.average_stress_level.toFixed(1)}%
              </Typography>
            </Box>
          </Box>

          <Divider />

          {/* Trend Analysis */}
          <Box>
            <Stack direction="row" alignItems="center" spacing={1} mb={1}>
              <ShowChartIcon fontSize="small" color="action" />
              <Typography variant="body2" fontWeight="500">
                {t('prediction.trend_analysis', 'Trend Analysis')}
              </Typography>
            </Stack>
            <Stack direction="row" spacing={2} alignItems="center">
              <Chip
                icon={getTrendIcon(prediction.trend)}
                label={getTrendLabel(prediction.trend)}
                sx={{
                  bgcolor: `${getTrendColor(prediction.trend)}20`,
                  color: getTrendColor(prediction.trend),
                  fontWeight: 600,
                }}
              />
              {prediction.trend_percentage !== 0 && (
                <Typography
                  variant="body2"
                  sx={{
                    color: getTrendColor(prediction.trend),
                    fontWeight: 500,
                  }}
                >
                  {prediction.trend_percentage > 0 ? '+' : ''}{prediction.trend_percentage.toFixed(1)}%
                </Typography>
              )}
            </Stack>
          </Box>

          {/* Peak Stress Hours */}
          {prediction.peak_stress_hours && prediction.peak_stress_hours.length > 0 && (
            <>
              <Divider />
              <Box>
                <Stack direction="row" alignItems="center" spacing={1} mb={1}>
                  <AccessTimeIcon fontSize="small" color="action" />
                  <Typography variant="body2" fontWeight="500">
                    {t('prediction.peak_hours', 'Peak Stress Hours')}
                  </Typography>
                </Stack>
                <Stack direction="row" spacing={1} flexWrap="wrap">
                  {prediction.peak_stress_hours.map((hour) => (
                    <Chip
                      key={hour}
                      label={`${hour.toString().padStart(2, '0')}:00`}
                      size="small"
                      variant="outlined"
                      color="warning"
                    />
                  ))}
                </Stack>
              </Box>
            </>
          )}

          {/* Confidence Level */}
          <Divider />
          <Box>
            <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1}>
              <Typography variant="body2" color="text.secondary">
                {t('prediction.confidence', 'Prediction Confidence')}
              </Typography>
              <Typography
                variant="caption"
                sx={{
                  color: getConfidenceColor(prediction.confidence),
                  fontWeight: 600,
                  textTransform: 'uppercase',
                }}
              >
                {t(`prediction.confidence_${prediction.confidence}`, prediction.confidence)}
              </Typography>
            </Stack>
            <LinearProgress
              variant="determinate"
              value={getConfidenceValue(prediction.confidence)}
              sx={{
                height: 6,
                borderRadius: 1,
                bgcolor: theme.palette.grey[200],
                '& .MuiLinearProgress-bar': {
                  bgcolor: getConfidenceColor(prediction.confidence),
                  borderRadius: 1,
                }
              }}
            />
            <Typography variant="caption" color="text.secondary" display="block" mt={0.5}>
              {t('prediction.based_on', 'Based on {{count}} data points', { count: prediction.data_points })}
            </Typography>
          </Box>
        </Stack>
      </CardContent>
    </Card>
  );
}

export default StressPrediction;
